import React, { useState, useRef, useEffect } from 'react';
import { DocumentBlock, TextBlock } from '../ast/document.types';
import { Move } from 'lucide-react';

interface SelectableBlockWrapperProps {
  block: DocumentBlock;
  sectionId: string;
  isSelected: boolean;
  isManualEditMode: boolean;
  onSelect: (blockId: string, sectionId: string) => void;
  onUpdateText?: (sectionId: string, blockId: string, newText: string) => void;
  onNudge?: (sectionId: string, blockId: string, dx: number, dy: number) => void;
  onDelete?: (sectionId: string, blockId: string) => void;
  onDuplicate?: (sectionId: string, blockId: string) => void;
  children: React.ReactNode;
}

export const SelectableBlockWrapper: React.FC<SelectableBlockWrapperProps> = ({
  block,
  sectionId,
  isSelected,
  isManualEditMode,
  onSelect,
  onUpdateText,
  onNudge,
  onDelete,
  onDuplicate,
  children,
}) => {
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const [editText, setEditText] = useState(() => (block.type === 'text' ? (block as TextBlock).data.text : ''));
  const isDraggingRef = useRef(false);
  const dragStartPos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Sync editText if block text changes externally
  useEffect(() => {
    if (block.type === 'text') {
      setEditText((block as TextBlock).data.text);
    }
  }, [block]);

  // If manual edit mode is disabled, render plain child
  if (!isManualEditMode) {
    return <>{children}</>;
  }

  const handlePointerDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    onSelect(block.id, sectionId);

    // Prepare drag movement
    isDraggingRef.current = true;
    dragStartPos.current = { x: e.clientX, y: e.clientY };

    const handlePointerMove = (moveEvent: PointerEvent) => {
      if (!isDraggingRef.current) return;
      const dx = moveEvent.clientX - dragStartPos.current.x;
      const dy = moveEvent.clientY - dragStartPos.current.y;

      // Nudge if moved more than 4px
      if (Math.abs(dx) >= 3 || Math.abs(dy) >= 3) {
        if (onNudge) {
          onNudge(sectionId, block.id, Math.round(dx / 2), Math.round(dy / 2));
        }
        dragStartPos.current = { x: moveEvent.clientX, y: moveEvent.clientY };
      }
    };

    const handlePointerUp = () => {
      isDraggingRef.current = false;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (block.type === 'text') {
      setIsInlineEditing(true);
    }
  };

  const handleSaveInlineText = () => {
    setIsInlineEditing(false);
    if (onUpdateText && block.type === 'text') {
      onUpdateText(sectionId, block.id, editText);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isInlineEditing) {
      if (e.key === 'Escape') {
        setIsInlineEditing(false);
        setEditText((block as TextBlock).data.text);
      }
      return;
    }

    if (isSelected) {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        if (onDelete) onDelete(sectionId, block.id);
      } else if (e.key === 'd' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (onDuplicate) onDuplicate(sectionId, block.id);
      } else if (e.key.startsWith('Arrow') && onNudge) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 2;
        if (e.key === 'ArrowLeft') onNudge(sectionId, block.id, -step, 0);
        if (e.key === 'ArrowRight') onNudge(sectionId, block.id, step, 0);
        if (e.key === 'ArrowUp') onNudge(sectionId, block.id, 0, -step);
        if (e.key === 'ArrowDown') onNudge(sectionId, block.id, 0, step);
      }
    }
  };

  const getBlockTypeLabel = () => {
    switch (block.type) {
      case 'text':
        return 'Text';
      case 'shape':
        return `Shape: ${(block as any).data?.shapeType || 'box'}`;
      case 'formula':
        return 'LaTeX Formula';
      case 'table':
        return 'Table';
      case 'image':
        return 'Figure';
      default:
        return 'Element';
    }
  };

  return (
    <div
      className={`selectable-block-wrapper ${isSelected ? 'selected' : ''}`}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      style={{
        position: 'relative',
        outline: isSelected ? '1.5px solid #2563eb' : '1px dashed transparent',
        outlineOffset: '2px',
        borderRadius: 'var(--radius-xs)',
        cursor: isSelected ? 'move' : 'pointer',
        transition: 'outline-color 0.1s ease',
        userSelect: isInlineEditing ? 'text' : 'none',
      }}
    >
      {/* Subtle Element Type Badge on Selection */}
      {isSelected && (
        <div
          style={{
            position: 'absolute',
            top: '-18px',
            left: '2px',
            backgroundColor: '#2563eb',
            color: '#ffffff',
            fontSize: '9px',
            fontWeight: 700,
            padding: '1px 6px',
            borderRadius: '2px',
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            zIndex: 15,
            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
            textTransform: 'capitalize',
            pointerEvents: 'none',
          }}
        >
          <Move size={8} />
          <span>{getBlockTypeLabel()}</span>
        </div>
      )}

      {/* Inline Text Editor for double-clicked text */}
      {isInlineEditing && block.type === 'text' ? (
        <textarea
          autoFocus
          className="form-input text-xs"
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onBlur={handleSaveInlineText}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              handleSaveInlineText();
            }
          }}
          style={{
            width: '100%',
            minHeight: '60px',
            fontSize: (block as TextBlock).data.formatting?.fontSizePt ? `${(block as TextBlock).data.formatting?.fontSizePt}pt` : 'inherit',
            fontFamily: (block as TextBlock).data.formatting?.fontFamily || 'inherit',
            lineHeight: 1.5,
            padding: '4px',
            border: '2px solid #2563eb',
            borderRadius: '2px',
            backgroundColor: '#ffffff',
            zIndex: 20,
          }}
        />
      ) : (
        children
      )}
    </div>
  );
};
